/**
 * Lightweight JWT claim reader. We don't verify the signature here — that's
 * the API's job; the client only needs to surface the customer's identity so
 * the portal can call `GET /v1/customers/:id`.
 *
 * Production swaps the dev-paste JWT for a service-identity-issued token; this helper
 * keeps working as long as the `sub` (or `customerId`) claim is set.
 *
 * The list of claim keys we accept is intentionally generous so a developer
 * pasting any reasonable test JWT (sub / customerId / customer_id / cid) lands
 * on the dashboard without re-issuing tokens.
 */
export interface CustomerClaims {
  /** Customer UUID. The portal calls `GET /v1/customers/:id`. */
  readonly customerId: string;
  /**
   * Identity-user UUID (`upmp_user_id` claim). This is the canonical key for
   * the citizen's NIPOST Pay wallet — the citizen self-service endpoints
   * (`/v1/wallet/wallets-mine` etc.) and the service-mail booking flow both
   * key the wallet's `customer_id` column off this value, NOT off the
   * customer-profile `customerId` above. Wallet-related pages in the portal
   * MUST use this for `getOrCreateWallet(...)` lookups, otherwise top-ups
   * land in one wallet (keyed by customer-profile id) while booking debits
   * a parallel empty wallet (keyed by upmp_user_id) — surfacing as
   * "insufficient balance" 402s on parcel booking.
   */
  readonly upmpUserId: string;
  /** Best-effort display name. May be empty — the layout falls back to "Customer". */
  readonly displayName: string;
  /** Best-effort email — Paystack popup needs it as the receipt recipient.
   *  Empty string when the JWT carries no email-shaped claim. */
  readonly email: string;
}

const CUSTOMER_ID_KEYS = ['customerId', 'customer_id', 'cid', 'sub'] as const;
const UPMP_USER_ID_KEYS = ['upmp_user_id', 'upmpUserId', 'sub'] as const;
const NAME_KEYS = ['name', 'fullName', 'preferred_username', 'email'] as const;
const EMAIL_KEYS = ['email', 'preferred_username'] as const;

function base64UrlDecode(segment: string): string {
  // Convert base64url to standard base64.
  let s = segment.replace(/-/g, '+').replace(/_/g, '/');
  // Pad to a multiple of 4.
  while (s.length % 4 !== 0) {
    s += '=';
  }
  // `atob` is universal in browsers and Node ≥16; the type is defined on
  // `globalThis` in lib.dom + lib.es2020. The web-customer app exclusively
  // targets browsers + jsdom (vitest) so a Node-only fallback isn't required.
  return atob(s);
}

function decodeUtf8(binary: string): string {
  // atob returns a binary string; reinterpret as UTF-8 so accented names survive.
  try {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return binary;
  }
}

export function decodeCustomerClaims(token: string | null | undefined): CustomerClaims | null {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  const payloadSegment = parts[1];
  if (!payloadSegment) {
    return null;
  }
  let parsed: Record<string, unknown>;
  try {
    const json = decodeUtf8(base64UrlDecode(payloadSegment));
    parsed = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }

  let customerId = '';
  for (const key of CUSTOMER_ID_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value.length > 0) {
      customerId = value;
      break;
    }
  }
  if (customerId.length === 0) {
    return null;
  }

  let upmpUserId = '';
  for (const key of UPMP_USER_ID_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value.length > 0) {
      upmpUserId = value;
      break;
    }
  }
  if (upmpUserId.length === 0) {
    upmpUserId = customerId;
  }

  let displayName = '';
  for (const key of NAME_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value.length > 0) {
      displayName = value;
      break;
    }
  }

  let email = '';
  for (const key of EMAIL_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value.includes('@') && value.length > 3) {
      email = value;
      break;
    }
  }

  return { customerId, upmpUserId, displayName, email };
}
